import { normalizeAlternateEmail } from "@/utils/alternateEmail";
import { toLanguageRows } from "@/utils/languageRequirements";
import { websiteValue } from "@/utils/website";

const toNumber = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? null : n;
};

/**
 * Turns the edit-profile form's values into the candidate-update-resume
 * request body (shared by the preview-retrigger dry run and the real PATCH).
 * Kept in its own module (rather than inline in index.jsx) so it's
 * unit-testable without pulling in the page's heavy transitive imports
 * (ResumePreview -> react-pdf, etc.) — the one rule it guards is that
 * `alternate_email` lands as a top-level key, never nested inside
 * `personal_information`.
 *
 * Languages ride as the paired-editor's own row shape ([{ language,
 * proficiency }]) — the backend's `LanguageProficiencyIn` reads that shape
 * directly. Sending the composite-string form ("English — Fluent") instead
 * would fold the whole string into `language` with `proficiency` lost.
 */
export const buildResumePayload = (formData) => ({
  personal_information: {
    full_name: formData.personal_information.name,
    email: formData.personal_information.email,
    phone: formData.personal_information.phone,
    location: formData.personal_information.location,
    website: websiteValue(formData.personal_information.portfolio),
    linkedin: formData.personal_information.linkedin,
  },
  alternate_email: normalizeAlternateEmail(formData.personal_information.alternate_email),
  summary: formData.personal_information.summary,
  skills: formData.skills.list,
  languages: toLanguageRows(formData.skills.languages),
  education: formData.education,
  work_experience: formData.experience.map((exp) => ({
    title: exp.jobTitle,
    company: exp.company,
    location: exp.location,
    start_date: exp.startDate,
    end_date: exp.endDate,
    description: exp.description,
    achievements: exp.achievements,
  })),
  additional_sections: formData.additionalSections.flatMap((sec) =>
    (sec.items || [])
      .map((entry) => ({
        section_title: sec.section_title,
        fields: Object.fromEntries((Array.isArray(entry) ? entry : [entry]).filter((i) => i?.key?.trim() && i?.value?.trim()).map((i) => [i.key, i.value])),
      }))
      .filter((s) => Object.keys(s.fields).length > 0),
  ),
  current_salary: toNumber(formData.compensation?.current_salary),
  expected_salary: toNumber(formData.compensation?.expected_salary),
  notice_period: formData.compensation?.notice_period || null,
  total_experience: toNumber(formData.compensation?.total_experience),
});
